import type { Board } from "./[boardId].page";
import Markdown from "react-markdown";
import { useState } from "react";
import { createPortal } from "react-dom";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FullscreenOutlined,
  QuestionCircleFilled,
} from "@ant-design/icons";
import { Button, Checkbox, Dropdown, Input, Radio, Tooltip, theme } from "antd";
import { DashboardV1State, MarkdownCard } from "pages/api/deep-links/types";
import { ToasterMessageType, useToasterContext } from "src/hooks/useToaster";
import { cloneData } from "src/utils/objects";
import { Flex } from "src/components/Flex";
import { CardEditor } from "../dashboards/CardEditor";

export function MarkdownViewer({
  cardId,
  json,
  colorTheme,
  board,
  onArrange,
}: {
  colorTheme?: string;
  json: Pick<DashboardV1State, "data">;
  cardId: string;
  board: Board;
  onArrange(): void;
}) {
  const toaster = useToasterContext();
  const card = json.data[cardId];
  if (card.type !== "markdown") throw new Error("Expected markdown card");

  const [editing, setEditing] = useState(false);
  const { token } = theme.useToken();
  return (
    <>
      <div
        className="hover-show"
        style={{
          backgroundColor: card.transparent
            ? "transparent"
            : colorTheme === "alternate"
            ? token.colorBgContainer
            : token.colorBgLayout,
          borderRadius: 10,
          height: "100%",
          overflow: "auto",
          position: "relative",
          width: "100%",
        }}
      >
        <div
          style={{
            paddingBottom: 0,
            paddingLeft: 10,
            paddingRight: 10,
            paddingTop: 14,
          }}
        >
          <Markdown className="markdown" skipHtml={true}>
            {card.markdown}
          </Markdown>
        </div>
        <div
          className="hover-show-target"
          style={{
            position: "absolute",
            top: 2,
            right: 2,
          }}
        >
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "rearrange",
                  label: "Move or resize",
                  icon: <FullscreenOutlined />,
                  disabled: !board.userCanEdit,
                  onClick() {
                    onArrange();
                  },
                },
                {
                  key: "edit",
                  label: "Edit markdown card",
                  icon: <EditOutlined />,
                  disabled: !board.userCanEdit,
                  onClick() {
                    setEditing(true);
                  },
                },
                {
                  key: "delete",
                  label: "Remove card from board",
                  icon: <DeleteOutlined />,
                  danger: true,
                  disabled: !board.userCanEdit,
                  onClick() {
                    if (!board.link) return;
                    const proceed = confirm(
                      "Do you want to delete this card from the board?"
                    );
                    if (proceed) {
                      const newData = { ...board.link.json.data };
                      delete newData[cardId];
                      const newLayout = board.link.json.layout.filter(
                        (layout) => layout.i !== cardId
                      );
                      return board
                        .save({
                          id: board.link.id,
                          name: board.link.name,
                          description: board.link.description,
                          scope: board.link.userId
                            ? "private"
                            : board.link.orgId
                            ? "workspace"
                            : "global",
                          ...board.link.json,
                          data: newData,
                          layout: newLayout,
                        })
                        .then(() => {
                          toaster.addMessage(ToasterMessageType.SUCCESS, {
                            message: "Removed card",
                          });
                        });
                    }
                  },
                },
              ],
            }}
          >
            <Button
              size="small"
              shape="round"
              type="text"
              style={{ padding: "0 3px" }}
            >
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>
      {editing &&
        createPortal(
          <MarkdownEditor
            card={card}
            onClose={() => setEditing(false)}
            onChange={(cardData) => {
              if (!board.link) return;
              const newData = { ...board.link.json.data, [cardId]: cardData };
              return board
                .save({
                  id: board.link.id,
                  name: board.link.name,
                  description: board.link.description,
                  scope: board.link.userId
                    ? "private"
                    : board.link.orgId
                    ? "workspace"
                    : "global",
                  ...board.link.json,
                  data: newData,
                })
                .then(() => {
                  toaster.addMessage(ToasterMessageType.SUCCESS, {
                    message: "Saved markdown card",
                  });
                });
            }}
          />,

          document.querySelector("#screen-insert") || document.body
        )}
    </>
  );
}

function MarkdownEditor({
  card,
  onClose,
  onChange,
}: {
  card: MarkdownCard;
  onClose(): void;
  onChange(cardData: DashboardV1State["data"][string]): void;
}) {
  const { token } = theme.useToken();
  const [isPreviewing, setPreviewing] = useState(false);
  const [working, updateWorking] = useState(cloneData(card));
  return (
    <CardEditor
      title="Markdown editor"
      onCancel={() => onClose()}
      okText="Save card"
      onOk={() => {
        onChange(working);
        onClose();
      }}
    >
      <Flex
        direction="column"
        style={{
          maxHeight: "100%",
          overflow: "auto",
          height: "100%",
          width: "100%",
        }}
      >
        <Flex spaceBetween gap={8} style={{ marginBottom: 16, width: "100%" }}>
          <Flex gap={16}>
            <Radio.Group
              value={isPreviewing}
              onChange={(e) => setPreviewing(e.target.value)}
              buttonStyle={"solid"}
              size="small"
            >
              <Radio.Button value={false}>
                <EditOutlined />
              </Radio.Button>
              <Radio.Button value={true}>
                <EyeOutlined />
              </Radio.Button>
            </Radio.Group>
            <Checkbox
              style={{ whiteSpace: "nowrap" }}
              checked={working.transparent}
              onChange={(e) =>
                updateWorking({ ...working, transparent: e.target.checked })
              }
            >
              Use transparent background
            </Checkbox>
          </Flex>
          <Tooltip placement="leftTop" title={<CheatSheet />}>
            <QuestionCircleFilled
              style={{
                cursor: "help",
                color: token.colorTextQuaternary,
              }}
            />
          </Tooltip>
        </Flex>
        <div
          style={{
            display: "flex",
            flex: 1,
            gap: 16,
            height: "100%",
            maxHeight: "100%",
            minHeight: 0,
            overflow: "auto",
            width: "100%",
          }}
        >
          <div style={{ flex: 1, height: "100%", minHeight: 0 }}>
            {isPreviewing ? (
              <Markdown className="markdown" skipHtml={true}>
                {working.markdown}
              </Markdown>
            ) : (
              <Input.TextArea
                style={{ height: "100%" }}
                value={working.markdown}
                onChange={(e) => {
                  updateWorking({ ...working, markdown: e.target.value });
                }}
              />
            )}
          </div>
        </div>
      </Flex>
    </CardEditor>
  );
}

function CheatSheet() {
  return (
    <div>
      <h1># Heading 1</h1>
      <h2>## Heading 2</h2>
      <h3>### Heading 3</h3>
      <h4>#### Heading 4</h4>
      <h5>##### Heading 5</h5>
      <h6>###### Heading 6</h6>
      --- Divider <hr style={{ display: "inline-block", width: 100 }} />
      <br />
      **<em> Bold </em>**
      <br />*<em> Italics </em>*<br />
      {"`"}
      <code>monospace</code>
      {"`"}
      <br />
      <div style={{ marginLeft: 8, marginTop: 4 }}>
        - Bullet List Item
        <br />- Bullet List Item
      </div>
      <div style={{ marginLeft: 8, marginTop: 4 }}>
        1. Numbered List
        <br />
        2. Numbered List
        <br />
      </div>
      <blockquote>{"> blockquote"}</blockquote>
    </div>
  );
}
